import React, { useContext, useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Typography, Grid, Box } from "@mui/material";
import toast from "react-hot-toast";
import { ThemeContext } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import BidValue from '../../components/global/BidAsk/BidValue';
import AskValue from '../../components/global/BidAsk/AskValue';
import { shallowEqual, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const CloseByHedgeModal = ({ open, onClose, selectedPosition, closePositionBy_Hedge }) => {
    const positions = useSelector((state) => state.orderPosition.positions);
    const [oppositePositionId, setOppositePositionId] = useState("");
    const { darkMode } = useContext(ThemeContext)
    const { t } = useTranslation()
    const [amount, setAmount] = useState(selectedPosition?.amount)
    const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams, shallowEqual);
    const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);
    const symbolParameterData = selectedPosition.symbolId ? allSymbolParams[selectedPosition.symbolId] : {};

    useEffect(() => {
        if (selectedPosition) {
            const opposite = positions.find(
                (pos) => pos.symbolId === selectedPosition.symbolId && pos.side !== selectedPosition.side
            );

            if (opposite) {
                setOppositePositionId(opposite.id);
            } else {
                setOppositePositionId("");
                toast.error("No opposite position available for hedging.");
            }
        }
    }, [selectedPosition, positions]);

    const handleCloseByHedge = () => {
        if (!selectedPosition || !oppositePositionId) {
            toast.error("No opposite position available for hedging.");
            return;
        }

        const inputAmount = Number(amount);
        const oppositePosition = positions.find(pos => pos.id === oppositePositionId);

        if (!oppositePosition) {
            toast.error("Selected opposite position not found.");
            return;
        }

        if (isNaN(inputAmount) || inputAmount <= 0) {
            toast.error("Amount must be a valid number greater than 0.");
            return;
        }

        if (inputAmount > oppositePosition.amount) {
            toast.error(`The amount is invalid. It should be less than or equal to ${oppositePosition.amount}.`);
            return;
        }

        closePositionBy_Hedge(selectedPosition.id, oppositePositionId, inputAmount);
        onClose();
    };


    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle sx={{ color: "white", p: 1 }} className="bg-primary">
                <Typography variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center" >{t('closeByHedge')}</Typography>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>{t('bid')}</Typography>
                        <Box sx={{ display: "flex", justifyContent: 'center' }}>
                            <BidValue symbolId={selectedPosition?.symbolId} digits={selectedPosition?.digits} />
                        </Box>
                    </Grid>
                    <Grid item xs={6} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>{t('ask')}</Typography>
                        <Box sx={{ display: "flex", justifyContent: 'center' }}>
                            <AskValue symbolId={selectedPosition?.symbolId} digits={selectedPosition?.digits} />
                        </Box>
                    </Grid>
                    {/* <Grid item xs={6}>
                        <TextField label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth />
                    </Grid> */}
                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: 13 }}>{t('amount')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid', borderColor: 'divider', backgroundColor: darkMode ? "#1e2533" : "#fff" }}>
                            <Button variant="" size="small" disabled={Number(amount) <= symbolParameterData.minimumAmountPerDeal} sx={{ borderRadius: 0, "&.Mui-disabled": { opacity: 1, backgroundColor: darkMode ? "#3a3f45" : "#e0e0e0" } }}
                                onClick={() => {
                                    setAmount((prev) => {
                                        const newAmount = Math.max(symbolParameterData.minimumAmountPerDeal, Number(prev) - symbolParameterData.minimumAmountPerDeal);
                                        return newAmount.toFixed(decimalPlaces);
                                    });
                                }}><RemoveIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} /></Button>

                            <TextField fullWidth type="number" value={amount ||''} onChange={(e) => setAmount(e.target.value)}
                                inputProps={{
                                    min: symbolParameterData.minimumAmountPerDeal,
                                    max: selectedPosition?.amount ?? undefined,
                                    step: symbolParameterData.minimumAmountPerDeal,
                                    style: { textAlign: 'center', }
                                }}
                                sx={{"& .MuiInputBase-input": { textAlign: "center", }, "& .MuiOutlinedInput-notchedOutline": { border: "none" }, color: darkMode ? 'var(--lightText)' : "var(--black)", p: 0, }}
                            />

                            <Button variant="" size="small" disabled={Number(amount) >= Number(selectedPosition?.amount)} sx={{ borderRadius: 0, "&.Mui-disabled": { opacity: 1, backgroundColor: darkMode ? "#3a3f45" : "#e0e0e0" } }}
                                onClick={() => {
                                    setAmount((prev) => {
                                        const newAmount = Math.min(Number(selectedPosition?.amount), Number(prev) + symbolParameterData.minimumAmountPerDeal);
                                        return newAmount.toFixed(decimalPlaces);
                                    });
                                }}>
                                <AddIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={6} mt={3}>
                        <TextField select label="Close By Hedge (Opposite Position)" value={oppositePositionId} onChange={(e) => setOppositePositionId(e.target.value)} fullWidth >
                            {positions
                                .filter((pos) => pos.symbolId === selectedPosition?.symbolId && pos.side !== selectedPosition?.side)
                                .map((pos) => (
                                    <MenuItem key={pos.id} value={pos.id}>{pos.id}</MenuItem>
                                ))}
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                <Button onClick={onClose} variant="outlined">{t('cancel')}</Button>
                <Button onClick={handleCloseByHedge} color="primary" variant="contained">{t('closeByHedge')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CloseByHedgeModal;
